import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { QrCode, ShieldCheck, FilePdf, ArrowSquareOut, Warning, CircleNotch, PlayCircle } from "@phosphor-icons/react";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ytId = (url) => {
  const m = (url || "").match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? m[1] : null;
};

export default function PublicMachine() {
  const { id } = useParams();
  const { t, lang, toggle } = useLang();
  const [machine, setMachine] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/public/machines/${id}`)
      .then(({ data }) => { setMachine(data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [id]);

  const docCard = (type) => {
    const available = machine[type];
    const link = machine[`${type}_link`];
    return (
      <div className={`rounded-md border p-5 ${available ? "border-emerald-500/30 bg-white/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={`public-doc-${type}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-[4px] ${available ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-slate-500"}`}>
              <FilePdf size={20} />
            </div>
            <div>
              <p className="font-semibold text-sm text-white">{t.pub[type]}</p>
              <p className="text-xs text-slate-400 mt-0.5">{available ? "PDF" : t.pub.notAvailable}</p>
            </div>
          </div>
          {available && (
            <a href={link} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors shrink-0"
              data-testid={`public-open-${type}`}>
              {t.pub.open} <ArrowSquareOut size={14} weight="bold" />
            </a>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white" data-testid="public-machine-page">
      <header className="border-b border-white/10">
        <div className="mx-auto max-w-2xl px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="public-logo">
            <div className="flex h-8 w-8 items-center justify-center bg-emerald-500 rounded-[4px]"><QrCode size={18} weight="bold" className="text-[#04170F]" /></div>
            <span className="font-display font-bold text-[17px] tracking-tight">Certifico<span className="text-emerald-500">QR</span></span>
          </Link>
          <button onClick={toggle} className="font-mono-tech text-xs font-semibold border border-white/20 rounded-[4px] px-2 py-1 text-slate-300" data-testid="public-lang-toggle">{lang.toUpperCase()}</button>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-10">
        {status === "loading" && (
          <div className="flex justify-center py-24"><CircleNotch size={32} className="text-emerald-500 animate-spin" /></div>
        )}

        {status === "notfound" && (
          <div className="text-center py-20" data-testid="public-not-found">
            <Warning size={48} className="mx-auto text-amber-400" />
            <h1 className="mt-6 font-display font-bold text-2xl">{t.pub.notFoundTitle}</h1>
            <p className="mt-2 text-sm text-slate-400">{t.pub.notFoundSub}</p>
            <Link to="/" className="mt-8 inline-flex rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-[#04170F] hover:bg-emerald-400 transition-colors">{t.pub.backHome}</Link>
          </div>
        )}

        {status === "ok" && machine && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-500">{t.pub.overline}</p>
                <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl tracking-tight" data-testid="public-machine-name">{machine.name}</h1>
                <p className="mt-1.5 text-slate-400">{machine.manufacturer} · {machine.model}</p>
              </div>
              {machine.has_logo && (
                <span className="shrink-0 inline-flex items-center bg-white rounded-[4px] px-2.5 py-2 mt-1" data-testid="public-client-logo">
                  <img src={`${API}/public/machines/${id}/logo`} alt={machine.manufacturer} className="h-9 w-auto max-w-[130px] object-contain" />
                </span>
              )}
            </div>

            {machine.has_photo && (
              <div className="mt-8 overflow-hidden rounded-md border border-white/10" data-testid="public-machine-photo">
                <img src={`${API}/public/machines/${id}/photo`} alt={machine.name} className="w-full max-h-80 object-cover" />
              </div>
            )}

            {machine.description && (
              <p className="mt-6 text-sm leading-relaxed text-slate-300" data-testid="public-machine-description">{machine.description}</p>
            )}

            {machine.video_url && (ytId(machine.video_url) ? (
              <div className="mt-8 overflow-hidden rounded-md border border-white/10 aspect-video" data-testid="public-machine-video">
                <iframe src={`https://www.youtube.com/embed/${ytId(machine.video_url)}`} title={machine.name}
                  className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            ) : (
              <a href={machine.video_url} target="_blank" rel="noreferrer"
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-5 py-2.5 text-sm font-semibold text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                data-testid="public-machine-video-link">
                <PlayCircle size={18} weight="fill" /> {t.pub.watchVideo} <ArrowSquareOut size={14} />
              </a>
            ))}

            <div className="mt-8 rounded-md border border-white/10 bg-white/[0.03] overflow-hidden" data-testid="public-specs">
              <div className="px-5 py-3 border-b border-white/10 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-slate-500">{t.pub.specs}</div>
              <div className="grid grid-cols-2">
                {[
                  [t.pub.manufacturer, machine.manufacturer],
                  [t.pub.model, machine.model],
                  [t.pub.serial, machine.serial_number],
                  [t.pub.year, machine.year || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="px-5 py-4 border-b border-r border-white/5 [&:nth-child(even)]:border-r-0 [&:nth-last-child(-n+2)]:border-b-0">
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.15em] text-slate-500">{k}</p>
                    <p className="mt-1 font-mono-tech text-[15px] text-white break-all">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            <h2 className="mt-10 font-display font-bold text-lg">{t.pub.docsTitle}</h2>
            <div className="mt-4 space-y-3">
              {docCard("manual")}
              {docCard("declaration")}
            </div>

            <div className="mt-10 flex items-start gap-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-4">
              <ShieldCheck size={20} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed text-slate-300">{t.pub.verified}</p>
            </div>

            <p className="mt-6 text-center font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-600">
              ID {machine.id.slice(0, 8)} · {t.pub.registered} {new Date(machine.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}
            </p>
          </motion.div>
        )}
      </main>
    </div>
  );
}
